import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

interface ThemeToggleProps {
  className?: string;
  iconClassName?: string;
  /** Render the current mode's label next to the icon (used in expanded nav/menu rows). */
  showLabel?: boolean;
  /** Extra callback fired after toggling -- e.g. closing a popover/drawer that wraps this. */
  onToggled?: () => void;
}

// Sun/moon toggle used in both the public marketing header (CompanyHeader) and the
// logged-in app nav (AppNav's various desktop/mobile exports). Kept as one shared
// component so the icon, a11y labelling, and click behavior stay consistent everywhere.
const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '', iconClassName = 'w-4 h-4', showLabel = false, onToggled }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  const handleClick = () => {
    toggleTheme();
    onToggled?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={className}
    >
      {isDark ? <Sun className={iconClassName} /> : <Moon className={iconClassName} />}
      {showLabel && (isDark ? 'Light mode' : 'Dark mode')}
    </button>
  );
};

export default ThemeToggle;
